import { lazy, Suspense, useEffect } from "react";
import { Agentation } from "agentation";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AuthProvider } from "@/lib/auth";
import { AppProvider } from "@/lib/store";
import { RoutineActivity } from "@/components/playlist/RoutineActivity";
import { routineVideoEnabled } from "@/lib/routineFeature";

import { CreatePerformanceModal } from "./components/dashboard/CreatePerformanceModal";
import { SaveToPerformanceModal } from "./components/dashboard/SaveToPerformanceModal";
import { AppSeoGuard } from "./components/seo/AppSeoGuard";
import { HashScrollManager } from "./components/routing/HashScrollManager";
import { ProtectedRoute } from "./components/routing/ProtectedRoute";
import { AdminRoute } from "./components/routing/AdminRoute";

const AdminPage = lazy(() => import("./pages/AdminPage"));
const FinalVideoEditorPage = lazy(() => import("./pages/FinalVideoEditorPage"));

// --- Public pages ---
import { LandingPage } from "@/pages/LandingPage";
import { DanceLibraryPage } from "@/pages/DanceLibraryPage";
import { AboutUsPage } from "./pages/AboutUsPage";
import { BackstageTalkPage } from "./pages/BackstageTalkPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { ContactUsPage } from "./pages/ContactUsPage";
import { PrivacyAndTermsPage } from "./pages/PrivacyAndTermsPage";
import { SavedPage } from "./pages/SavedPage";
import { RoutinePage } from "@/pages/RoutinePage";
import { TutorialPage } from "@/pages/TutorialPage";

// --- Auth pages ---
import { LoginPage } from "./pages/LoginPage";
import { SignUpPage } from "./pages/SignUpPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { ConfirmationPage } from "./pages/ConfirmationPage";

// --- Protected pages (dashboard) ---
import { DashboardPage } from "./pages/DashboardPage";
import { CoursePage } from "./pages/CoursePage";
import { PlaylistPage } from "./pages/PlaylistPage";
import { SettingsPage } from "./pages/SettingsPage";
import { AddOnsPage } from "./pages/AddOnsPage";

// --- Shared pages (public, no auth required) ---
import { SharedTutorialPage } from "./pages/SharedTutorialPage";
import { SharedPlaylistPage } from "./pages/SharedPlaylistPage";
import { SharedPlaylistCoursePage } from "./pages/SharedPlaylistCoursePage";

// ---------------------------------------------------------------------------

const importMetaEnv = (import.meta as ImportMeta & {
  env?: Record<string, string | boolean | undefined>;
}).env;

const processEnv =
  typeof globalThis !== "undefined" && "process" in globalThis
    ? (globalThis as typeof globalThis & {
        process?: { env?: Record<string, string | undefined> };
      }).process?.env
    : undefined;

const isDevelopment =
  String(importMetaEnv?.DEV) === "true" ||
  importMetaEnv?.MODE === "development" ||
  processEnv?.NODE_ENV === "development";

const isAgentationEnabled = isDevelopment;

// ---------------------------------------------------------------------------
// PostHog analytics
// ---------------------------------------------------------------------------

const posthogKey = importMetaEnv?.PUBLIC_POSTHOG_KEY ?? processEnv?.PUBLIC_POSTHOG_KEY;
const posthogHost =
  importMetaEnv?.PUBLIC_POSTHOG_HOST ??
  processEnv?.PUBLIC_POSTHOG_HOST ??
  "https://us.i.posthog.com";

if (posthogKey && !posthog.__loaded) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    defaults: "2026-01-30",
  });
}

// ---------------------------------------------------------------------------

function usePreventTopOverscroll() {
  useEffect(() => {
    if (typeof window === "undefined" || !("ontouchstart" in window)) {
      return;
    }

    let startX = 0;
    let startY = 0;

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      if (!touch) {
        return;
      }

      startX = touch.clientX;
      startY = touch.clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      if (!touch) {
        return;
      }

      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;

      if (deltaY <= 0 || Math.abs(deltaY) <= Math.abs(deltaX)) {
        return;
      }

      if (window.scrollY <= 0) {
        event.preventDefault();
      }
    };

    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);
}

export function App() {
  usePreventTopOverscroll();

  const showAgentation = isAgentationEnabled;

  return (
    <PostHogProvider client={posthog}>
      <BrowserRouter>
        <AuthProvider>
          <AppProvider>
            <HashScrollManager />
            <AppSeoGuard />
            <Routes>
              {/* ── Public pages ────────────────────────────────────── */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/dance-library" element={<DanceLibraryPage />} />
              <Route path="/backstage-talk" element={<BackstageTalkPage />} />
              <Route path="/blog-post/:slug" element={<BlogPostPage />} />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/contact-us" element={<ContactUsPage />} />
              <Route path="/privacy-and-terms-of-use" element={<PrivacyAndTermsPage />} />
              <Route path="/saved" element={<SavedPage />} />

              {/* ── Tutorial/routine detail (public, checkout requires auth) ─ */}
              <Route path="/app/tutorial/:id" element={<TutorialPage />} />
              <Route path="/app/routine/:id" element={<RoutinePage />} />

              {/* ── Auth pages (redirect to dashboard if logged in) ── */}
              <Route path="/app/auth/login" element={<LoginPage />} />
              <Route path="/app/auth/sign-up" element={<SignUpPage />} />
              <Route path="/app/auth/forget-password" element={<ForgotPasswordPage />} />
              <Route path="/app/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/app/auth/confirmation" element={<ConfirmationPage />} />

              {/* ── Protected dashboard pages ────────────────────── */}
              <Route
                path="/app/dashboard/home"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/dashboard/course"
                element={
                  <ProtectedRoute>
                    <CoursePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/dashboard/final-video-editor"
                element={<ProtectedRoute><Suspense fallback={<div>Loading editor…</div>}><FinalVideoEditorPage /></Suspense></ProtectedRoute>}
              />
              <Route
                path="/app/dashboard/playlist"
                element={
                  <ProtectedRoute>
                    <PlaylistPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/dashboard/add-ons"
                element={
                  <ProtectedRoute>
                    <AddOnsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/dashboard/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />

              {/* ── Admin ───────────────────────────────────────── */}
              <Route
                path="/app/admin"
                element={
                  <AdminRoute>
                    <Suspense
                      fallback={
                        <div className="flex min-h-screen items-center justify-center">
                          <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-dark border-t-transparent" />
                        </div>
                      }
                    >
                      <AdminPage />
                    </Suspense>
                  </AdminRoute>
                }
              />

              {/* ── Shared pages (public, no auth) ───────────────── */}
              <Route path="/app/shared-tutorial" element={<SharedTutorialPage />} />
              <Route path="/app/shared-playlist" element={<SharedPlaylistPage />} />
              <Route path="/app/course-shared" element={<SharedPlaylistCoursePage />} />

              {/* ── Fallback ─────────────────────────────────────── */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <CreatePerformanceModal />
            <SaveToPerformanceModal />
            {routineVideoEnabled && <RoutineActivity />}
            {showAgentation && <Agentation />}
          </AppProvider>
        </AuthProvider>
      </BrowserRouter>
    </PostHogProvider>
  );
}

export default App;
